export type CompletionReceiptRecord = {
  payload: {
    id: string;
    assurance: string;
    completionSummary?: unknown;
  };
};

/**
 * Keep append-only history inspectable without duplicating a stable record.
 * Current records win when a transport repeats the same identity in both
 * compatibility buckets.
 */
export function combineCompletionHistory<TCurrent, THistorical>(
  current: readonly TCurrent[],
  incomplete: readonly THistorical[],
  stableId: (record: TCurrent | THistorical) => string,
): Array<TCurrent | THistorical> {
  const byId = new Map<string, TCurrent | THistorical>();
  for (const record of [...current, ...incomplete]) {
    const id = stableId(record);
    if (!byId.has(id)) byId.set(id, record);
  }
  return [...byId.values()];
}

export function isRetrospectiveHistoryReceipt(receipt: CompletionReceiptRecord): boolean {
  return "completionSummary" in receipt.payload && receipt.payload.assurance === "RETROSPECTIVE";
}

export function retrospectiveCompletionHistory<
  TCurrent extends CompletionReceiptRecord,
  THistorical extends CompletionReceiptRecord,
>(current: readonly TCurrent[], incomplete: readonly THistorical[]): Array<TCurrent | THistorical> {
  return combineCompletionHistory(current, incomplete, (receipt) => receipt.payload.id).filter(
    isRetrospectiveHistoryReceipt,
  );
}

export function currentProspectiveReceipts<TReceipt extends CompletionReceiptRecord>(
  current: readonly TReceipt[],
): TReceipt[] {
  return current.filter((receipt) => receipt.payload.assurance === "PROSPECTIVE");
}

export function currentAuthorityIds<TRecord>(
  current: readonly TRecord[],
  stableId: (record: TRecord) => string,
): Set<string> {
  return new Set(current.map(stableId));
}

export function canAuthorizeCurrentAction(
  recordId: string,
  currentIds: ReadonlySet<string>,
  prerequisitesMet = true,
): boolean {
  return prerequisitesMet && currentIds.has(recordId);
}
